import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("light", "kana");

type Locale = "ko" | "en";
type Row = [string, string, string, string];

const LOCALE_STORAGE_KEY = "kanatomo-privacy-locale";
const locales: Locale[] = ["ko", "en"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN" };

const dataRows: Record<Locale, Row[]> = {
  ko: [
    ["로컬 학습 데이터", "학습 진도, 단어 학습 기록", "복습/통계 기능 제공", "기기 내 (Hive Box)"],
    ["Firebase Remote Config / Crashlytics", "앱 버전, 충돌 로그, 기기 정보(익명)", "안정성 개선 및 오류 분석", "Firebase (Google LLC)"],
    ["광고 SDK (AdMob)", "광고 식별자, 언어/국가 정보(익명)", "광고 표시 및 통계 분석", "Google AdMob 서버"],
  ],
  en: [
    ["Local study data", "Study progress and vocabulary history", "Review and statistics features", "On device (Hive Box)"],
    ["Firebase Remote Config / Crashlytics", "App version, crash logs, anonymous device information", "Stability improvement and error analysis", "Firebase (Google LLC)"],
    ["Ad SDK (AdMob)", "Advertising identifier, anonymous language/country information", "Ad delivery and analytics", "Google AdMob servers"],
  ],
};

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    tableHead: Row;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    websiteStats: { title: string; body: string; list: string[] };
  }
> = {
  ko: {
    crumb: "KanaTomo Privacy",
    title: "KanaTomo,",
    accentTitle: "개인정보 처리방침",
    subtitle:
      "최종 업데이트일: 2025년 10월 21일. 본 정책의 운영 주체는 Daydream Factory이며, KanaTomo는 사용자 식별을 위한 회원가입 기능 없이 동작하고 학습 기능 제공과 안정성 개선에 필요한 최소한의 데이터만 처리합니다.",
    tableHead: ["구분", "수집 항목", "사용 목적", "저장 위치"],
    sections: [
      {
        title: "1. 수집하는 정보",
      },
      {
        title: "2. 데이터 사용 목적",
        list: [
          "학습 데이터 저장 및 복습 기능 제공",
          "앱 오류 및 성능 개선",
          "서비스 업데이트를 위한 원격 설정 관리",
          "광고 표시 및 통계 분석",
        ],
      },
      {
        title: "3. 제3자 제공",
        body: "KanaTomo는 사용자 정보를 외부에 판매하거나 임의로 공유하지 않습니다. 단, 아래 제3자 서비스가 익명 데이터를 처리할 수 있습니다.",
        list: ["Firebase (Google LLC)", "Google AdMob (Google LLC)"],
      },
      {
        title: "4. 데이터 보관 및 삭제",
        list: [
          "로컬 데이터는 사용자 기기 내에만 저장되며 앱 삭제 시 즉시 제거됩니다.",
          "Firebase 및 AdMob 로그는 각 서비스 정책에 따라 일정 기간 후 자동 삭제됩니다.",
        ],
      },
      {
        title: "5. 사용자 권리 및 보안",
        list: [
          "앱을 삭제하면 모든 로컬 데이터가 함께 삭제됩니다.",
          "iOS / Android 설정에서 광고 추적 제한 기능을 설정할 수 있습니다.",
          "Hive AES 암호화, HTTPS 통신, ATS 정책 준수를 통해 보안을 유지합니다.",
          "문의: support@daydreamfactory.net",
        ],
      },
    ],
    websiteStats: {
      title: "6. 웹사이트 방문 통계",
      body:
        "Daydream Factory 웹사이트 및 KanaTomo 랜딩 페이지에서는 방문 흐름과 페이지 이용 현황을 익명 수준으로 확인하기 위해 Firebase Analytics (Google LLC)를 사용할 수 있습니다.",
      list: [
        "수집 예시: 페이지 경로, 방문 시각, 브라우저/기기 유형, 대략적 지역 정보",
        "사용 목적: 랜딩 페이지 개선, 유입 경로 확인, 스토어 버튼 전환 분석",
        "해당 통계는 웹사이트 방문 시에만 적용되며, 앱 내 로컬 학습 기록과는 분리됩니다.",
      ],
    },
  },
  en: {
    crumb: "KanaTomo Privacy",
    title: "KanaTomo,",
    accentTitle: "Privacy Policy",
    subtitle:
      "Last updated: October 21, 2025. This policy is operated by Daydream Factory. KanaTomo works without account registration and processes only the minimum data needed to provide learning features and improve stability.",
    tableHead: ["Category", "Collected data", "Purpose", "Storage"],
    sections: [
      {
        title: "1. Information We Collect",
      },
      {
        title: "2. How We Use Data",
        list: [
          "To save study data and provide review features",
          "To improve app stability and performance",
          "To manage remote configuration for service updates",
          "To support ads and related analytics",
        ],
      },
      {
        title: "3. Third-Party Services",
        body: "KanaTomo does not sell or arbitrarily share user information. However, the following third-party services may process anonymous data.",
        list: ["Firebase (Google LLC)", "Google AdMob (Google LLC)"],
      },
      {
        title: "4. Data Retention and Deletion",
        list: [
          "Local data is stored only on the user's device and is removed when the app is deleted.",
          "Firebase and AdMob logs are automatically deleted according to each provider's policies.",
        ],
      },
      {
        title: "5. User Rights and Security",
        list: [
          "Deleting the app removes local data stored with it.",
          "You can limit ad tracking in iOS or Android settings.",
          "Security is maintained through Hive AES encryption, HTTPS, and ATS-compliant communication.",
          "Contact: support@daydreamfactory.net",
        ],
      },
    ],
    websiteStats: {
      title: "6. Website Visit Analytics",
      body:
        "The Daydream Factory website and KanaTomo landing page may use Firebase Analytics (Google LLC) to understand visit flow and page usage at an anonymous level.",
      list: [
        "Examples: page path, visit time, browser/device type, and approximate region",
        "Purpose: improve landing pages, understand acquisition flow, and measure store button conversion",
        "These statistics apply only to website visits and are separate from in-app local study records.",
      ],
    },
  },
};

const PrivacyPolicy: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("ko");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "ko"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.sections[0].title}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {copy.tableHead.map((item) => (
                      <th key={item} style={styles.thtd}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows[locale].map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => (
                        <td key={cell} style={styles.thtd}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {copy.sections.slice(1).map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? <p style={styles.body}>{section.body}</p> : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>
                      {item.includes("support@daydreamfactory.net") ? (
                        <>
                          {item.replace("support@daydreamfactory.net", "")}
                          <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                            support@daydreamfactory.net
                          </a>
                        </>
                      ) : item === "Firebase (Google LLC)" ? (
                        <>
                          Firebase (Google LLC) -{" "}
                          <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Firebase Privacy &amp; Security
                          </a>
                        </>
                      ) : item === "Google AdMob (Google LLC)" ? (
                        <>
                          Google AdMob (Google LLC) -{" "}
                          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={styles.link}>
                            Google Privacy Policy
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.websiteStats.title}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>{copy.websiteStats.body}</p>
            <ul style={styles.list}>
              {copy.websiteStats.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
